import lodashIsFinite from 'lodash/isFinite';
import uniq from 'lodash/uniq';
import isNil from 'lodash/isNil';
import round from 'lodash/round';
import { CheckCircle, CrossCircle, SubtractCircle, PHX_CHECKBOX_STATES } from 'phx-core';

import { IScoreBarDistributionInputGraphData } from '@graphs/score-bar-distribution-graph/score-bar-distribution-graph.component';
import { IPageHeaderTab } from '@misc/page-header/page-header.component';
import { STEP_STATUS } from '@misc/steps/steps.component';
import { IPlayerDownloadSource } from '@misc/video-player/video-player.component';

import { IAdditionalCamera } from '@services/vehicles-with-camera-search';
import { Session } from '@services/session';
import { CoachingFeatureFlagService } from '@services/feature-flags/coaching';
import { DateFnsFormatter } from '@services/date-fns-formatter';

import { fullName, getDistance, kmToMiles, toMoment } from '@lib/utils';
import { IMoment, IMomentRange, SERVER_DATE_FORMAT, moment } from '@lib/moment';
import { BLUE_30, BLUE_60, ORANGE_40, YELLOW_20, BLACK_30 , GREEN_60 } from '@lib/colors';
import { CameraPosition, CameraType, isAIDashcamPlus, isDualFacingCamera } from '@lib/cam-types';
import { inDemoMode } from '@lib/remote-login';
import { replaceLastName } from '@lib/demo-mode/data-obfuscation';

import { IVehicleBehaviorSettings } from '../../admin/vehicles/vehicles-2.0/vehicle-behavior-settings/vehicle-behavior-settings.interface';
import { IBehaviorSettings } from '../../admin/safety/safety.interface';
import { IDpeNote, IResponseDpeNote, IViolationMetadata, IResponseViolationMetadata } from './events/events.interface';
import {
  ICameraImages,
  ICameraMedia,
  ICameraVideos,
  IMediaSource,
  IResponseCameraMedia,
  IResponseCameraMediaVideo,
  IVideoSourcesMeta,
  IResponseDownloadableVideos,
  IResponseDriver,
  IDriver,
} from '@lib/safety/safety.interface';
import {
  IDownloadableVideos,
} from '@app/dashboard/safety/events/detail/detail.interface';
import { IScoreDistribution } from '@app/dashboard/safety/common/safety.interface';
import { IWeekInfoDotTooltip } from '@lib/safety/safety.interface';
import { IPipEventPlayerInput } from '@app/misc/event-player/event-player.interface';
import { IResponseUser } from '@app/services/company.interface';
import { VideoCodecType } from '@lib/safety/safety.interface';
import {
  IEmsNumberInfo,
  Notification,
  INotificationUpdate,
  IResponseEmsNumberInfo,
  NOTIFICATION_TYPE,
} from '@safety/events/detail/severe-collision-card/severe-collision-card.interface';
import { IAssignedCoach, IGroup, IServerGroup, IResponseAssignedCoach } from '@lib/coaching/coaching.interface';
import { PhxSelectOption } from 'phx-core/lib/select/select.types';

import { CAMERA_POSITION_TO_LABEL, ImageSrcType, MediaSrcType, OBFUSCATED_PHONE_NUMBER, VideoSrcType } from '@lib/safety/safety.const';
import { ALL_EVENTS_BUCKET } from '@lib/safety/events/events.const';
import { CameraObstructionMap } from '@safety/events/event.const';
import { CAM_CATEGORY_LABELS } from '@app/dashboard/safety/cameras/cameras.consts';
import { DashcamStatus } from '@app/misc/dashcam/dashcam.constants';
import { SCORE_SCALE_COLORS, SCORE_SCALE } from '@app/misc/safety/safety-score.const';
import { ICONS } from '@app/map/marker-icons';
import { DpeDismissalReasonType } from '@misc/event-dismissal-modal/event-dismissal-modal.consts';
import { PREVIOUS_MOST_DATE, KPH_THRESHOLD, MPH_THRESHOLD } from '@lib/safety/safety.const';
import { VehicleClass } from '@lib/vehicles.const';
import { ALLOWED_COUNTRY_CODES, EMS_NOTIFICATION_LABELS } from '@safety/events/detail/severe-collision-card/severe-collision-card.const';
import { CHAT_CONSTANTS } from '@app/dashboard/messaging/messaging.constants';
import { UnsafeBehaviorType } from '@app/admin/safety/safety.consts';
import { PIP_EVENT_PLAYER_DOWNLOAD_KEYS } from '@misc/event-player/pip-event-player/pip-event-player.consts';

export const MPHPS_PER_GRAVITY: number = 21.9369;
export const KPHPS_PER_GRAVITY: number = 35.3039;

export const MIN_THRESHOLD: number = 0.28;
export const MAX_THRESHOLD: number = 0.46;
const MIN_CORNER_THRESHOLD: number = 0.38;
const MAX_CORNER_THRESHOLD: number = 0.7;

const INCREASE_TO_MAX_THRESHOLD_VALUE: number = 0.12;

const LIGHT_DUTY_HARD_ACCEL_MIN_THRESHOLD: number = 0.44;
const MEDIUM_DUTY_HARD_ACCEL_MIN_THRESHOLD: number = 0.41;
const HEAVY_DUTY_HARD_ACCEL_MIN_THRESHOLD: number = 0.38;

const LIGHT_DUTY_HARD_BRAKE_MIN_THRESHOLD: number = 0.44;
const MEDIUM_DUTY_HARD_BRAKE_MIN_THRESHOLD: number = 0.38;
const HEAVY_DUTY_HARD_BRAKE_MIN_THRESHOLD: number = 0.34;

const LIGHT_DUTY_HARD_CORNER_MIN_THRESHOLD: number = 0.44;
const MEDIUM_DUTY_HARD_CORNER_MIN_THRESHOLD: number = 0.42;
const HEAVY_DUTY_HARD_CORNER_MIN_THRESHOLD: number = 0.40;

const HARD_CORNER_MAX_THRESHOLD: number = 0.7; // for all vehicle class
const VIOLATION_METADATA_TIME_THRESHOLD: number = 10; // 10 seconds

export const MIN_DPE_AGE: number = 1;
export const MIN_DRIVE_SCORE_AGE: number = 2;
export const VIDEO_ANALYSIS_TIMEOUT: number = 72;  // 72 hours

export const HYPERLAPSE_MULTIPLIER = 60; // hyperlpase videos are 60 times fater than the normal video 60min = 1min
export const SINGLE_BEHAVIOR_MAX_SELECTABLE_OPTIONS: number = 5;

export const ENHANCED_VIDEO_MAX_DURATION_SEC: number = 180;

const SAFETY_URL_PREFIX: string = '/safety';

export const CONFIDENTIAL_ERROR_SERVER_MESSAGE: string = 'confidential';

export interface ISafetyTabsUrl {
  HOME: string;
  OVERVIEW: string;
  DRIVERS: string;
  COACHING: string;
  EVENTS: string;
  SPEEDING: string;
  REQUESTS: string;
  INSIGHTS: string;
  CAMERAS: string;
}

export const SAFETY_TABS_URL: Record<keyof ISafetyTabsUrl, string> = Object.freeze({
  HOME: SAFETY_URL_PREFIX,
  OVERVIEW: `${SAFETY_URL_PREFIX}/overview`,
  DRIVERS: `${SAFETY_URL_PREFIX}/drivers`,
  COACHING: `${SAFETY_URL_PREFIX}/coaching`,
  EVENTS: `${SAFETY_URL_PREFIX}/events`,
  SPEEDING: `${SAFETY_URL_PREFIX}/speeding`,
  REQUESTS: `${SAFETY_URL_PREFIX}/requests`,
  INSIGHTS: `${SAFETY_URL_PREFIX}/insights`,
  CAMERAS: `${SAFETY_URL_PREFIX}/cameras`,
});

export const SAFETY_ACTIONABLE_BREADCRUMB = {
  ROOT: { title: $localize`Safety`, commands: [SAFETY_TABS_URL.HOME] },
  DRIVERS: { title: $localize`Drivers`, commands: [SAFETY_TABS_URL.DRIVERS] },
  EVENTS: { title: $localize`Events`, commands: [SAFETY_TABS_URL.EVENTS] },
  SPEEDING: { title: $localize`Speeding`, commands: [SAFETY_TABS_URL.SPEEDING] },
  REQUESTS: { title: $localize`Requests`, commands: [SAFETY_TABS_URL.REQUESTS] },
  CUSTOM_SPEED_LIMITS: {
    title: $localize`Custom Speed Limits`,
    commands: [SAFETY_TABS_URL?.SPEEDING, 'custom-speed-limits'],
  },
};

export enum TABS {
  Overview = 'overview',
  Drivers = 'drivers',
  Coaching = 'coaching',
  Events = 'events',
  Speeding = 'speeding',
  Requests = 'requests',
  Insights = 'insights',
  Cameras = 'cameras',
}

export enum RECALL_VIDEO_TYPES {
  NORMAL = 'normal',
  HYPERLAPSE = 'hyperlapse',
  DPE_VIDEO_ATTACH = 'dpe_video_attach',
}

// All the values are in g
export const SAFETY_THRESHOLD_LIMITS = {
  hard_accel: {
    min: MIN_THRESHOLD,
    max: MAX_THRESHOLD,
  },
  hard_brake: {
    min: MIN_THRESHOLD,
    max: MAX_THRESHOLD,
  },
  hard_corner: {
    min: MIN_CORNER_THRESHOLD,
    max: MAX_CORNER_THRESHOLD,
  },
};

interface ISafetyThresholdRange {
  min: number;
  max: number;
}

type IVehicleClassSafetyThresholdsRange = {
  [key in VehicleClass]: ISafetyThresholdRange;
};

interface ISafetyThresholdLimits {
  hard_accel: IVehicleClassSafetyThresholdsRange,
  hard_brake: IVehicleClassSafetyThresholdsRange,
  hard_corner: IVehicleClassSafetyThresholdsRange
}

// All the values are in g
export const INCREASED_SAFETY_THRESHOLD_LIMITS: ISafetyThresholdLimits = {
  hard_accel: {
    LIGHT: {
      min: LIGHT_DUTY_HARD_ACCEL_MIN_THRESHOLD,
      max: LIGHT_DUTY_HARD_ACCEL_MIN_THRESHOLD + INCREASE_TO_MAX_THRESHOLD_VALUE,
    },
    MEDIUM: {
      min: MEDIUM_DUTY_HARD_ACCEL_MIN_THRESHOLD,
      max: MEDIUM_DUTY_HARD_ACCEL_MIN_THRESHOLD + INCREASE_TO_MAX_THRESHOLD_VALUE,
    },
    HEAVY: {
      min: HEAVY_DUTY_HARD_ACCEL_MIN_THRESHOLD,
      max: HEAVY_DUTY_HARD_ACCEL_MIN_THRESHOLD + INCREASE_TO_MAX_THRESHOLD_VALUE,
    },
  },
  hard_brake: {
    LIGHT: {
      min: LIGHT_DUTY_HARD_BRAKE_MIN_THRESHOLD,
      max: LIGHT_DUTY_HARD_BRAKE_MIN_THRESHOLD + INCREASE_TO_MAX_THRESHOLD_VALUE,
    },
    MEDIUM: {
      min: MEDIUM_DUTY_HARD_BRAKE_MIN_THRESHOLD,
      max: MEDIUM_DUTY_HARD_BRAKE_MIN_THRESHOLD + INCREASE_TO_MAX_THRESHOLD_VALUE,
    },
    HEAVY: {
      min: HEAVY_DUTY_HARD_BRAKE_MIN_THRESHOLD,
      max: HEAVY_DUTY_HARD_BRAKE_MIN_THRESHOLD + INCREASE_TO_MAX_THRESHOLD_VALUE,
    },
  },
  hard_corner: {
    LIGHT: {
      min: LIGHT_DUTY_HARD_CORNER_MIN_THRESHOLD,
      max: HARD_CORNER_MAX_THRESHOLD,
    },
    MEDIUM: {
      min: MEDIUM_DUTY_HARD_CORNER_MIN_THRESHOLD,
      max: HARD_CORNER_MAX_THRESHOLD,
    },
    HEAVY: {
      min: HEAVY_DUTY_HARD_CORNER_MIN_THRESHOLD,
      max: HARD_CORNER_MAX_THRESHOLD,
    },
  },
};

export enum COACHING_HISTORY_ITEM_TYPE {
  SESSION = 'session',
  EVENT = 'event',
  SAFETY_RECAP = 'safety_recap',
}

export enum SAFETY_THRESHOLD_SLIDER_STEPS {
  hard_accel = 0.1,
  hard_brake = 0.1,
  hard_corner = 0.01,
}

export enum IntensityUnit {
  Acceleration = 'acceleration',
  G = 'g',
  S = 's',
  Speed = 'speed',
  Kph = 'kph',
  Percent = '%',
}

export enum DpeTrigger {
  Anomaly = 'anomaly',
  Gps = 'gps',
  Fusion = 'fusion',
  Hypothesis = 'hypothesis',
  Hubble = 'hubble',
  Telematics = 'telematics',
  DpeCandidate = 'dpe_candidate',
  CollisionCandidate = 'collision_candidate',
  Omnicam = 'omnicam',
}

export enum DpeFrequency {
  _1Hz = 1,
  _2Hz = 2,
}

function is1HzDpeTrigger(trigger: DpeTrigger): boolean {
  const _1HzDpeTriggers: DpeTrigger[] = [
    DpeTrigger.Anomaly,
    DpeTrigger.Gps,
    DpeTrigger.Fusion,
    DpeTrigger.Telematics,
    DpeTrigger.DpeCandidate,
    DpeTrigger.CollisionCandidate,
    DpeTrigger.Omnicam, // Adding Omnicam to 1Hz DPE triggers since it is unknown for now.
  ];
  return _1HzDpeTriggers.includes(trigger);
}

function is2HzDpeTrigger(trigger: DpeTrigger): boolean {
  const _2HzDpeTriggers: DpeTrigger[] = [DpeTrigger.Hypothesis, DpeTrigger.Hubble];
  return _2HzDpeTriggers.includes(trigger);
}

export function getDpeTriggerFrequency(trigger: DpeTrigger): number {
  if (is1HzDpeTrigger(trigger)) {
    return DpeFrequency._1Hz;
  }
  if (is2HzDpeTrigger(trigger)) {
    return DpeFrequency._2Hz;
  }
  throw new Error(`Unknown DPE trigger: ${trigger}`);
}

export function isLegacyTrigger(trigger: DpeTrigger): boolean {
  const legacyTriggers: DpeTrigger[] = [DpeTrigger.Gps, DpeTrigger.Fusion];
  return legacyTriggers.includes(trigger);
}

export function isTriggerHubble(trigger: DpeTrigger): boolean {
  return trigger === DpeTrigger.Hubble;
}

export enum DriveScoreContributor {
  hard_brake = 'hard_brake',
  hard_accel = 'hard_accel',
  hard_corner = 'hard_corner',
  speeding = 'speeding',
  hos_violation = 'hos_violation',
}

export interface IDriveScoreContributors {
  hardAccel: number;
  hardBrake: number;
  hardCorner: number;
  speeding: number;
  hosViolation: number;
}

export interface IDriveScoreContributorLabels {
  hard_brake: string;
  hard_accel: string;
  hard_corner: string;
  speeding: string;
  hos_violation: string;
}

export type SafetyTag = string;

export interface ITelematicsEventTypes {
  hard_brake: SafetyTag,
  hard_accel: SafetyTag,
  hard_corner: SafetyTag,
  seat_belt_violation: SafetyTag,
}

export const TELEMATICS_EVENT_TYPES: ITelematicsEventTypes = {
  hard_brake: 'hard_brake',
  hard_accel: 'hard_accel',
  hard_corner: 'hard_corner',
  seat_belt_violation: 'seat_belt_violation',
};

const TELEMATICS_EVENT_TYPES_LABELS: ITelematicsEventTypes = {
  hard_brake: $localize`Hard braking`,
  hard_accel: $localize`Hard acceleration`,
  hard_corner: $localize`Hard cornering`,
  seat_belt_violation: $localize`Seat belt violation`,
};

export interface IPeriodEndParam {
  period_end?: string;
}

export interface IPeriodDates {
  previousPeriodStartDate: IMoment;
  previousPeriodEndDate: IMoment;
  nextPeriodEndDate: IMoment;
}

export interface IResponseCurrentTimeRange {
  current_period_start: string;
  current_period_end: string;
}

export interface ICurrentTimeRange {
  currentPeriodStart: IMoment;
  currentPeriodEnd: IMoment;
}

export interface IAIVisionEventTypes {
  tailgating: SafetyTag,
}

export const AI_VISION_EVENT_TYPES: IAIVisionEventTypes = {
  tailgating: 'tailgating',
};

// Used to show the text inside AI Vision Toggle of the Event Player
export const AI_VISION_EVENT_TYPES_LABELS: IAIVisionEventTypes = {
  tailgating: $localize`Time-to-Hit`,
};

export interface IResponseConfidentialMetadata {
  is_downstream_updated?: boolean;
  marked_by: {
    id: number;
    first_name: string;
    last_name: string;
  };
  marked_at: string;
}

export interface IConfidentialMetadata {
  markedBy: string;
  markedAt: string;
}

export interface IAIEventTypes {
  unsafe_lane_change: SafetyTag;
  tailgating: SafetyTag,
  cell_phone: SafetyTag,
  stop_sign_violation: SafetyTag,
  distraction: SafetyTag,
  drowsiness: SafetyTag;
  seat_belt_violation: SafetyTag,
  camera_obstruction: SafetyTag,
  road_facing_cam_obstruction: SafetyTag,
  driver_facing_cam_obstruction: SafetyTag,
  forward_collision_warning: SafetyTag,
  unsafe_parking: SafetyTag,
  zone_detection: SafetyTag,
  aggregated_lane_swerving: SafetyTag;
  smoking: SafetyTag;
}

export const AI_EVENT_TYPES: IAIEventTypes = {
  unsafe_lane_change: 'unsafe_lane_change',
  tailgating: 'tailgating',
  cell_phone: 'cell_phone',
  stop_sign_violation: 'stop_sign_violation',
  distraction: 'distraction',
  drowsiness: 'drowsiness',
  seat_belt_violation: 'seat_belt_violation',
  camera_obstruction: 'camera_obstruction',
  road_facing_cam_obstruction: 'road_facing_cam_obstruction',
  driver_facing_cam_obstruction: 'driver_facing_cam_obstruction',
  forward_collision_warning: 'forward_collision_warning',
  unsafe_parking: 'unsafe_parking',
  zone_detection: 'zone_detection',
  aggregated_lane_swerving: 'aggregated_lane_swerving',
  smoking: 'smoking',
};

const AI_EVENT_TYPES_LABELS: IAIEventTypes = {
  unsafe_lane_change: $localize`Unsafe lane change`,
  tailgating: $localize`Close following`,
  cell_phone: $localize`Cell phone usage`,
  stop_sign_violation: $localize`Stop sign violation`,
  distraction: $localize`Distraction`,
  drowsiness: $localize`Drowsiness`,
  seat_belt_violation: $localize`Seat belt violation`,
  camera_obstruction: $localize`Obstructed camera`,
  road_facing_cam_obstruction: $localize`Obstructed camera`,
  driver_facing_cam_obstruction: $localize`Obstructed camera`,
  forward_collision_warning: $localize`Forward collision warning`,
  unsafe_parking: $localize`Unsafe parking`,
  zone_detection: $localize`Zone detection`,
  aggregated_lane_swerving: $localize`Lane swerving`,
  smoking: $localize`Smoking`,
};

interface IOtherEventTypes {
  crash: SafetyTag;
  manual_event: SafetyTag;
  speeding: SafetyTag;
}

const OTHER_EVENT_TYPES: IOtherEventTypes = {
  crash: 'crash',
  manual_event: 'manual_event',
  speeding: 'speeding',
};

const OTHER_EVENT_TYPES_LABELS: IOtherEventTypes = {
  crash: $localize`Collision`,
  manual_event: $localize`Driver capture`,
  speeding: $localize`Speeding`,
};

export interface IEventTypes extends ITelematicsEventTypes, IAIEventTypes, IOtherEventTypes { }

export type EventType = keyof IEventTypes;

export const EVENT_TYPES: IEventTypes = {
  ...TELEMATICS_EVENT_TYPES,
  ...AI_EVENT_TYPES,
  ...OTHER_EVENT_TYPES,
};

export const EVENT_TYPES_LABELS: IEventTypes = {
  ...TELEMATICS_EVENT_TYPES_LABELS,
  ...AI_EVENT_TYPES_LABELS,
  ...OTHER_EVENT_TYPES_LABELS,
};

// Event types that cannot be video tags
const NON_TAGGABLE_EVENT_TYPES: Partial<IEventTypes> = {
  [EVENT_TYPES.hard_brake]: EVENT_TYPES.hard_brake,
  [EVENT_TYPES.hard_accel]: EVENT_TYPES.hard_accel,
  [EVENT_TYPES.hard_corner]: EVENT_TYPES.hard_corner,
  [EVENT_TYPES.manual_event]: EVENT_TYPES.manual_event,
  [EVENT_TYPES.zone_detection]: EVENT_TYPES.zone_detection,
};

export function getSelfCoachingCompliantEventBehaviors(): Partial<IEventBehaviors> {
  const { zone_detection, ...SELF_COACHING_AI_EVENT_TYPES } = AI_EVENT_TYPES;
  const { crash, ...SELF_COACHING_OTHER_EVENT_TYPES } = OTHER_EVENT_TYPES;

  return {
    ...TELEMATICS_EVENT_TYPES,
    ...SELF_COACHING_AI_EVENT_TYPES,
    ...SELF_COACHING_OTHER_EVENT_TYPES,
  };
}

export function isHardEvent(eventType: EventType): boolean {
  switch (eventType) {
  case EVENT_TYPES.hard_brake:
  case EVENT_TYPES.hard_accel:
  case EVENT_TYPES.hard_corner:
    return true;
  default:
    return false;
  }
}

/**
 * Events that cannot be video tags are not taggable
 * @param eventType Event type
 * @returns true if the event type is taggable
 */
export function isTaggableEventType(eventType: EventType): boolean {
  return !Boolean(NON_TAGGABLE_EVENT_TYPES[eventType]);
}

interface IRoadFacingTags {
  crash: SafetyTag;
  near_miss: SafetyTag;
  tailgating: SafetyTag;
  unsafe_lane_change: SafetyTag;
  cut_off: SafetyTag;
  stop_sign_violation: SafetyTag;
  ran_a_red_light: SafetyTag;
  camera_obstruction: SafetyTag,
  road_facing_cam_obstruction: SafetyTag,
}

interface IOmnicamVideoTags {
  blind_spot_detection: SafetyTag;
}

export const ROAD_FACING_TAGS: IRoadFacingTags = {
  crash: 'crash',
  near_miss: 'near_miss',
  tailgating: 'tailgating',
  unsafe_lane_change: 'unsafe_lane_change',
  cut_off: 'cut_off',
  stop_sign_violation: 'stop_sign_violation',
  ran_a_red_light: 'ran_a_red_light',
  camera_obstruction: 'camera_obstruction',
  road_facing_cam_obstruction: 'road_facing_cam_obstruction',
};

const OMNICAM_VIDEO_TAGS: IOmnicamVideoTags = {
  blind_spot_detection: 'blind_spot_detection',
};

const OMNICAM_VIDEO_TAGS_LABELS: IOmnicamVideoTags = {
  blind_spot_detection: $localize`Blind spot detection`,
};

export const ROAD_FACING_TAGS_LABELS: IRoadFacingTags = {
  tailgating: $localize`Close following`,
  cut_off: $localize`Lane cutoff`,
  unsafe_lane_change: $localize`Unsafe lane change`,
  near_miss: $localize`Near collision`,
  crash: $localize`Collision`,
  stop_sign_violation: $localize`Stop sign violation`,
  ran_a_red_light: $localize`Ran red light`,
  camera_obstruction: $localize`Obstructed camera`,
  road_facing_cam_obstruction: $localize`Obstructed camera`,
};

interface IDriverFacingTags {
  cell_phone: SafetyTag;
  distraction: SafetyTag;
  drowsiness: SafetyTag;
  seat_belt_violation: SafetyTag;
  smoking: SafetyTag;
  camera_obstruction: SafetyTag;
  driver_facing_cam_obstruction: string;
}

export const DRIVER_FACING_TAGS: IDriverFacingTags = {
  cell_phone: 'cell_phone',
  distraction: 'distraction',
  drowsiness: 'drowsiness',
  seat_belt_violation: 'seat_belt_violation',
  smoking: 'smoking',
  camera_obstruction: 'camera_obstruction',
  driver_facing_cam_obstruction: 'driver_facing_cam_obstruction',
};

export const DRIVER_FACING_TAGS_LABELS: IDriverFacingTags = {
  cell_phone: $localize`Cell phone usage`,
  distraction: $localize`Distraction`,
  drowsiness: $localize`Drowsiness`,
  seat_belt_violation: $localize`Seat belt violation`,
  smoking: $localize`Smoking`,
  camera_obstruction: $localize`Obstructed camera`,
  driver_facing_cam_obstruction: $localize`Obstructed camera`,
};

interface IOtherVideoTags {
  in_progress: SafetyTag,
  no_tag_applies: SafetyTag,
  notify_emergency_services: SafetyTag,
}

export const OTHER_VIDEO_TAGS: IOtherVideoTags = {
  in_progress: 'in_progress',
  no_tag_applies: 'no_tag_applies',
  notify_emergency_services: 'notify_emergency_services',
};

const OTHER_VIDEO_TAGS_LABELS: IOtherVideoTags = {
  in_progress: null,
  no_tag_applies: null,
  notify_emergency_services: null,
};

interface IPositiveVideoTags {
  safe_distancing: SafetyTag,
  alert_driving: SafetyTag,
}

export const POSITIVE_VIDEO_TAGS: IPositiveVideoTags = {
  safe_distancing: 'safe_distancing',
  alert_driving: 'alert_driving',
};

const POSITIVE_VIDEO_TAGS_LABELS: IPositiveVideoTags = {
  safe_distancing: $localize`Safe distancing`,
  alert_driving: $localize`Alert driving`,
};

export enum HIDDEN_RISK_FACTORS {
  camera_installation_issue = 'camera_installation_issue',
}

interface IVideoTags extends IDriverFacingTags,
  IRoadFacingTags,
  IOmnicamVideoTags,
  IOtherVideoTags,
  IPositiveVideoTags { }

export type VideoTagType = keyof IVideoTags;

export const VIDEO_TAGS: IVideoTags = {
  ...ROAD_FACING_TAGS,
  ...DRIVER_FACING_TAGS,
  ...OMNICAM_VIDEO_TAGS,
  ...OTHER_VIDEO_TAGS,
  ...POSITIVE_VIDEO_TAGS,
};

export const VIDEO_TAGS_LABELS: IVideoTags = {
  ...ROAD_FACING_TAGS_LABELS,
  ...DRIVER_FACING_TAGS_LABELS,
  ...OMNICAM_VIDEO_TAGS_LABELS,
  ...OTHER_VIDEO_TAGS_LABELS,
  ...POSITIVE_VIDEO_TAGS_LABELS,
};

export interface IEventBehaviors extends IEventTypes, IVideoTags { }

export type EventBehaviorType = keyof IEventBehaviors;

export const EVENT_BEHAVIORS: IEventBehaviors = {
  ...EVENT_TYPES,
  ...VIDEO_TAGS,
};

export const EVENT_BEHAVIORS_LABELS: IEventBehaviors = {
  ...EVENT_TYPES_LABELS,
  ...VIDEO_TAGS_LABELS,
};

interface ICoachableBehaviors {
  tailgating: SafetyTag,
  unsafe_lane_change: SafetyTag,
  distraction: SafetyTag,
  speeding: SafetyTag,
  drowsiness: SafetyTag,
  seat_belt_violation: SafetyTag,
  cell_phone: SafetyTag,
  ran_a_red_light: SafetyTag,
  stop_sign_violation: SafetyTag,
  smoking: SafetyTag,
  hard_brake: SafetyTag,
  hard_accel: SafetyTag,
  hard_corner: SafetyTag,
  manual_event: SafetyTag,
  crash: SafetyTag,
  forward_collision_warning: SafetyTag,
  unsafe_parking: SafetyTag,
  near_miss: SafetyTag,
  cut_off: SafetyTag,
  camera_obstruction: SafetyTag,
  aggregated_lane_swerving: SafetyTag,
}

// Order Matters
export const COACHABLE_BEHAVIORS: ICoachableBehaviors = {
  tailgating: EVENT_TYPES.tailgating,
  unsafe_lane_change: EVENT_TYPES.unsafe_lane_change,
  distraction: DRIVER_FACING_TAGS.distraction,
  speeding: EVENT_TYPES.speeding,
  drowsiness: DRIVER_FACING_TAGS.drowsiness,
  seat_belt_violation: EVENT_TYPES.seat_belt_violation,
  cell_phone: EVENT_TYPES.cell_phone,
  ran_a_red_light: ROAD_FACING_TAGS.ran_a_red_light,
  stop_sign_violation: ROAD_FACING_TAGS.stop_sign_violation,
  smoking: DRIVER_FACING_TAGS.smoking,
  cut_off: ROAD_FACING_TAGS.cut_off,
  // Following behaviors are coachable if these are the only behavior present in coachable DPE
  hard_accel: EVENT_TYPES.hard_accel,
  hard_brake: EVENT_TYPES.hard_brake,
  hard_corner: EVENT_TYPES.hard_corner,
  manual_event: EVENT_TYPES.manual_event,
  crash: EVENT_TYPES.crash,
  forward_collision_warning: AI_EVENT_TYPES.forward_collision_warning,
  unsafe_parking: AI_EVENT_TYPES.unsafe_parking,
  near_miss: ROAD_FACING_TAGS.near_miss,
  camera_obstruction: AI_EVENT_TYPES.camera_obstruction,
  aggregated_lane_swerving: AI_EVENT_TYPES.aggregated_lane_swerving,
};

export enum GraphToggleOption {
  DRIVE = 'drive_score',
  EVENT = 'event_rate',
  UNSAFE_BEHAVIORS = 'unsafe_behaviors',
  INDIVIDUAL_BEHAVIORS = 'individual_behaviors',
  SPEEDING = 'speeding_rate',
}

export enum SafetyTagsBucket {
  core_risk = 'core_risk',
  violations = 'violations',
  driver_awareness = 'driver_awareness',
  harsh_driving = 'harsh_driving',
}

export type SafetyTagsBucketCount = { [key in SafetyTagsBucket]: number };
type SafetyTagsBucketLabel = { [key in SafetyTagsBucket]: string };

export const SAFETY_TAGS_BUCKET_LABELS: SafetyTagsBucketLabel = {
  core_risk: $localize`Core risk`,
  violations: $localize`Traffic sig. & signs`,
  driver_awareness: $localize`Awareness`,
  harsh_driving: $localize`Harsh driving`,
};

export const SAFETY_TAGS_BUCKET_TOOLTIP: SafetyTagsBucketLabel = {
  core_risk: $localize`Fundamental driving principles with high correlation to accidents`,
  violations: $localize`Violations of posted signs and signals in local, state, or federal jurisdictions`,
  driver_awareness: $localize`Driver conduct that can potentially impact their driving`,
  harsh_driving: $localize`Behaviors generated due to sudden changes in direction and speed`,
};

export interface ISafetyTagBucket {
  key: SafetyTagsBucket,
  label: string,
  tooltip: string,
  tags: EventBehaviorType[],
  color: string,
}

export const SAFETY_TAG_BUCKETS: ISafetyTagBucket[] = [{
  key: SafetyTagsBucket.core_risk,
  label: SAFETY_TAGS_BUCKET_LABELS.core_risk,
  tooltip: SAFETY_TAGS_BUCKET_TOOLTIP.core_risk,
  tags: [
    'unsafe_lane_change',
    'tailgating',
    'cut_off',
    'aggregated_lane_swerving',
  ],
  color: BLUE_60,
}, {
  key: SafetyTagsBucket.violations,
  label: SAFETY_TAGS_BUCKET_LABELS.violations,
  tooltip: SAFETY_TAGS_BUCKET_TOOLTIP.violations,
  tags: [
    'ran_a_red_light',
    'stop_sign_violation',
    'speeding',
  ],
  color: BLUE_30,
}, {
  key: SafetyTagsBucket.driver_awareness,
  label: SAFETY_TAGS_BUCKET_LABELS.driver_awareness,
  tooltip: SAFETY_TAGS_BUCKET_TOOLTIP.driver_awareness,
  tags: [
    'seat_belt_violation',
    'distraction',
    'drowsiness',
    'smoking',
    'cell_phone',
    'camera_obstruction',
  ],
  color: YELLOW_20,
}, {
  key: SafetyTagsBucket.harsh_driving,
  label: SAFETY_TAGS_BUCKET_LABELS.harsh_driving,
  tooltip: SAFETY_TAGS_BUCKET_TOOLTIP.harsh_driving,
  tags: [
    'hard_brake',
    'hard_accel',
    'hard_corner',
  ],
  color: ORANGE_40,
}];

export const MOST_COMMON_OPTION_BUCKET: ISafetyTagBucket = {
  key: 'most_common' as SafetyTagsBucket,
  label: $localize`Most Common`,
  tags: null,
  tooltip: null,
  color: null,
};

export enum COACHING_STATUSES {
  all = 'all',
  pending_review = 'pending_review',
  coachable = 'coachable',
  coached = 'coached',
  dismissed = 'uncoachable', // Only key is changed, value remains same
}

export const DEFAULT_COACHING_STATUSES: COACHING_STATUSES[] = [
  COACHING_STATUSES.pending_review,
  COACHING_STATUSES.coachable,
  COACHING_STATUSES.coached,
];

export const COACHING_STATUS_LABELS: { [key in COACHING_STATUSES]?: string } = {
  pending_review: $localize`Pending review`,
  coachable: $localize`Coachable`,
  coached: $localize`Coached`,
  uncoachable: $localize`Dismissed`,
};

export enum BlurStatus {
  has_blur = 'has_blur',
  has_no_blur = 'has_no_blur',
  not_reviewed = 'not_reviewed',
}

export const BLUR_STATUS_LABELS = {
  all: $localize`All Blur Statuses`,
  has_blur: $localize`Has Blur`,
  has_no_blur: $localize`No Blur`,
  not_reviewed: $localize`Not Reviewed`,
};

export enum VIDEO_TYPE {
  default = 'default',
  annotated = 'annotated',
}

export enum Severity {
  critical = 'critical',
  high = 'high',
  medium = 'medium',
  low = 'low',
  pending = 'pending',
  na = 'na',
}

export type SeverityType = keyof typeof Severity;

export const SEVERITY_LABELS = Object.freeze({
  critical: $localize`Critical`,
  high: $localize`High`,
  medium: $localize`Medium`,
  low: $localize`Low`,
});

export const DEFAULT_SEVERITY_LABELS = Object.freeze({
  pending: $localize`Pending`,
  na: $localize`N/A`,
});

export enum MEDIA_REQUEST_STATUS {
  NOT_REQUESTED = 'not_requested',
  IN_PROGRESS = 'in_progress',
  FAILED = 'failed',
  SUCCESS = 'success',
}

export type PhoenixOptionsType =
  | typeof COACHING_STATUS_LABELS
  | typeof SEVERITY_LABELS
  | typeof BLUR_STATUS_LABELS
  | typeof CAM_CATEGORY_LABELS
  | DashcamStatus;

export function getMappedPhxOptions(options: PhoenixOptionsType): PhxSelectOption[] {
  const newOptions: PhxSelectOption[] = [];
  Object.entries(options).forEach(([value, label]): void => {
    newOptions.push({
      label,
      value,
    });
  });

  return newOptions;
}

const HARSH_EVENTS_WITHOUT_CORNERING: string[] = [
  EVENT_TYPES.hard_accel,
  EVENT_TYPES.hard_brake,
  EVENT_TYPES_LABELS.hard_accel, // Checking for labels since event types are transformed in Reports 2.0
  EVENT_TYPES_LABELS.hard_brake,
];

export function convertAcceleration(acceleration: number, eventType: string, metricUnits: boolean): number {
  const conversionFactor = metricUnits ? KPHPS_PER_GRAVITY : MPHPS_PER_GRAVITY;
  acceleration = acceleration || 0;
  if (HARSH_EVENTS_WITHOUT_CORNERING.includes(eventType)) {
    acceleration *= conversionFactor;
  }

  return acceleration;
}

export function convertAccelToGForce(accelInKph: number): number {
  const gForce: string = (accelInKph / KPHPS_PER_GRAVITY).toFixed(2);
  return Number(gForce);
}

/** Used to display consistent and rounded values in Admin/Safety */
export function mapAccelerationFromG(
  value: number,
  eventType: string,
  isMetricUnits: boolean,
): number {
  if (isNil(value)) return null;

  const DECIMAL_PLACES: number = 1;

  const convertedValue: number = convertAcceleration(value, eventType, isMetricUnits);
  return round(convertedValue, DECIMAL_PLACES);
}

export function formatAccelerationFromG(
  value: number,
  eventType: string,
  isMetricUnits: boolean,
): string {
  let unit: string;
  let convertedValue: number;

  if (HARSH_EVENTS_WITHOUT_CORNERING.includes(eventType)) {
    convertedValue = mapAccelerationFromG(value, eventType, isMetricUnits);
    unit = isMetricUnits ? 'kph/s' : 'mph/s';
  } else {
    convertedValue = value;
    unit = 'g';
  }

  return `${convertedValue} ${unit}`;
}

export function convertAccelerationBack(acceleration: number, eventType: string, metricUnits: boolean): number {
  const conversionFactor = metricUnits ? KPHPS_PER_GRAVITY : MPHPS_PER_GRAVITY;
  acceleration = acceleration || 1;
  if (eventType === EVENT_TYPES.hard_accel || EVENT_TYPES.hard_brake) {
    acceleration /= conversionFactor;
  }

  return acceleration;
}

export function formatAcceleration(acceleration, eventType, metricUnits) {
  acceleration = convertAcceleration(acceleration, eventType, metricUnits);

  let unit;
  if (HARSH_EVENTS_WITHOUT_CORNERING.includes(eventType)) {
    unit = metricUnits ? 'kph/s' : 'mph/s';
    acceleration = Number(acceleration.toFixed(1));
  } else {
    unit = 'g';
    acceleration = Number(acceleration.toFixed(2));
  }

  if (eventType === EVENT_TYPES.hard_brake || eventType === EVENT_TYPES_LABELS.hard_brake) {
    acceleration = `-${acceleration}`;
  }

  return `${acceleration} ${unit}`;
}

/*
* Assigns exact recommended threshold value from BE API to avoid unwanted offsets due to unit conversions. Thus, makes sure
* slider value is exactly aligned to recommended threshold bar.
*
* Example:
*   Recommended Threshold value from BE API = 0.30412g (10.7 kphs | 6.7 mphs)
*   converted back to G = 10.7 kphs -> 0.30308g | 6.7 mphs -> 0.3054g
*
*   Since, we will be storing one of these let's take the second one and convert again
*   0.3054g -> 10.8 kphs | 0.3054g -> 6.7 mphs
*
*   Offset of 0.1 for kphs value. Hence, we directly use BE ones without conversion to avoid this behaviour.
*/
export function preprocessSmartThresholdValue(
  eventType: string,
  metricUnits: boolean,
  thresholdValue: number,
  recommendedValueInGravity: number,
  // Needed for case when recommendedValue & converted recommendedValueInGravity are different i.e offset of 0.1
  recommendedValue: number = null,
  {
    minThreshold = MIN_THRESHOLD,
    maxThreshold = MAX_THRESHOLD,
  },
): number {
  recommendedValue = recommendedValue ? recommendedValue : convertAcceleration(recommendedValueInGravity, eventType, metricUnits);
  let thresholdValueInGravity = thresholdValue === recommendedValue ?
    recommendedValueInGravity : convertAccelerationBack(thresholdValue, eventType, metricUnits);

  if (thresholdValueInGravity < minThreshold) thresholdValueInGravity = minThreshold;
  if (thresholdValueInGravity > maxThreshold) thresholdValueInGravity = maxThreshold;

  return thresholdValueInGravity;
}

function _returnZeroIfNull(val) { return val != null ? val : 0; }

export function mapSafetySummary(summary, metricUnits) {
  summary = summary || {};
  const brakes = _returnZeroIfNull(summary.num_hard_brakes);
  const accelerations = _returnZeroIfNull(summary.num_hard_accels);
  const corners = _returnZeroIfNull(summary.num_hard_corners);
  const score = _returnZeroIfNull(summary.total_score);
  const ageInWeeks = summary.safety_score_age;
  const speedingEvents = _returnZeroIfNull(summary.num_speeding);
  const speedingTime = _returnZeroIfNull(summary.total_speeding_time);
  const drivingTime = _returnZeroIfNull(summary.total_driving_time);

  let distance = _returnZeroIfNull(summary.total_kilometers);
  if (!metricUnits) distance = kmToMiles(distance);

  let brakesAvg: any = 0;
  let accelerationsAvg: any = 0;
  let cornersAvg: any = 0;
  let speedingAvg: number = 0;
  if (distance) {
    brakesAvg = (brakes / distance * 100).toFixed(2);
    accelerationsAvg = (accelerations / distance * 100).toFixed(2);
    cornersAvg = (corners / distance * 100).toFixed(2);
  }

  if (drivingTime) {
    speedingAvg = (speedingTime / drivingTime) * 100;
  }

  return {
    brakes,
    brakesAvg,
    accelerations,
    accelerationsAvg,
    corners,
    cornersAvg,
    distance,
    score,
    ageInWeeks,
    speedingEvents,
    speedingTime,
    drivingTime,
    speedingAvg,
  };
}

/**
 * Special Case - show largest unit on every case instead of our consistent behaviour via duration pipe which at least
 * shows hh:mm:ss (and for days or above do roundOff), so for this special case we are manually rounding the extra seconds
 *
 * Example:
 * 1000 | duration (output => 3 hrs ago)
 * otherwise it will show (output => 2 hrs 46 min 20 sec)
 */
export function getLargestTimeUnit(timeInSec: number): number {
  const secondsInMinutes = 60;
  const secondsInHours = 60 * 60;
  const secondsInDays = 60 * 60 * 24;

  if (timeInSec <= secondsInDays) {
    if (timeInSec > secondsInHours) {
      timeInSec = completeLargestUnit(timeInSec, secondsInHours);
    } else if (timeInSec > secondsInMinutes) {
      timeInSec = completeLargestUnit(timeInSec, secondsInMinutes);
    }
  }

  return timeInSec;

  function completeLargestUnit(timeInSeconds: number, secToCompare: number): number {
    const extraSeconds = timeInSeconds % secToCompare;

    return Math.round(extraSeconds / secToCompare) > 0
      ? timeInSeconds + (secToCompare - extraSeconds)
      : timeInSeconds - extraSeconds;
  }
}

export interface IResponseEventIntensity {
  name: string;
  value: number | number[];
  unit_type: IntensityUnit;
}

export interface IEventIntensity {
  name: string;
  value: number;
  unitType: IntensityUnit;
}

export function mapIntensity(eventIntensity: IResponseEventIntensity): IEventIntensity {
  if (!eventIntensity) return null;

  const intensityValue: number = typeof eventIntensity.value === 'number' ? eventIntensity.value : 0;
  return {
    name: eventIntensity.name,
    value: intensityValue,
    unitType: eventIntensity.unit_type,
  };
}

export function mapNote(note: IResponseDpeNote, userTimeZone: string = null): IDpeNote {
  if (!note) return;

  // Dismissal Notes are auto generated at BE, but they don't store user info in DB
  if (note.dismissal_reasons?.length) {
    note.user = {
      id: -1,
      first_name: 'Motive',
      last_name: '',
    };
  }

  return {
    id: note.id,
    body: note.body,
    createdAt: userTimeZone ? moment(note.created_at).mapTz(userTimeZone) : moment(note.created_at),
    updatedAt: userTimeZone ? moment(note.updated_at).mapTz(userTimeZone) : moment(note.updated_at),
    isEdited: note.updated_at && !(moment(note.created_at).isSame(note.updated_at)),
    isVisibleToDriver: note.is_visible_to_driver,
    user: {
      id: note.user?.id,
      name: fullName(note.user),
    },
    dismissalReasons: note.dismissal_reasons,
    dismissedBy: note.dismissed_by ? {
      id: note.dismissed_by.id,
      name: fullName(note.dismissed_by),
    } : null,
  };
}

export function createMockDismissalNote(user: IResponseUser, dismissalReasons: DpeDismissalReasonType): IDpeNote {
  return {
    id: null,
    body: null,
    createdAt: moment(),
    updatedAt: null,
    isEdited: null,
    isVisibleToDriver: null,
    user: {
      id: null,
      name: 'Motive',
    },
    dismissalReasons,
    dismissedBy: {
      id: user.id,
      name: fullName(user),
    },
  };
}

export function mapCameraMedia(cameraMedia: IResponseCameraMedia): ICameraMedia {
  if (!cameraMedia) return null;

  return {
    id: cameraMedia.id,
    uploadedAt: moment(cameraMedia.uploaded_at),
    camType: cameraMedia.cam_type,
    images: mapCameraImages(cameraMedia),
    videos: mapCameraVideos(cameraMedia),
    additionalCamTypes: cameraMedia.additional_cam_types,
  };
}

export function getMp4PlayerSource(sources: IMediaSource[]): IMediaSource {
  return sources.find((source: IMediaSource): boolean => source.type === VideoSrcType.Mp4);
}

function getPlayerSource(srcType: MediaSrcType, srcUrl: string, codec?: VideoCodecType): IMediaSource {
  return { type: srcType, src: srcUrl, codec };
}

export const isHEVCMediaSource = (source: IMediaSource): boolean => source.codec === 'hevc';

export function mapCameraVideos(cameraMedia: IResponseCameraMedia): ICameraVideos {
  if (!cameraMedia) return;

  const cameraObjects: IResponseCameraMediaVideo[] = [
    cameraMedia.front_facing, cameraMedia.driver_facing, cameraMedia.front_narrow_facing,
    cameraMedia.side_left, cameraMedia.side_right, cameraMedia.rear,
  ];

  const isVideoAvailable: boolean = cameraObjects.some(camera => camera?.media_status === MEDIA_REQUEST_STATUS.SUCCESS);

  if (!isVideoAvailable) return;

  const getPlayerVideoSources = (cameraMediaVideo: IResponseCameraMediaVideo): IMediaSource[] => {
    const sources: IMediaSource[] = [];

    // If BE uses AWS MediaConvert for DPE videos transcoding then we will not get the WEBM url
    if (cameraMedia.webm_url) {
      sources.push(getPlayerSource(VideoSrcType.WebM, cameraMediaVideo.webm_url));
    }
    if (cameraMediaVideo.h265_url) {
      sources.push(getPlayerSource(VideoSrcType.Mp4, cameraMediaVideo.h265_url, 'hevc'));
    }
    sources.push(getPlayerSource(VideoSrcType.Mp4, cameraMediaVideo.mp4_url));

    return sources;
  };

  const mapCameraSource = (cameraMediaVideo: IResponseCameraMediaVideo, annotatedVideoUrl: string): IVideoSourcesMeta => {
    if (!cameraMediaVideo) return;

    const videoSourcesMeta: IVideoSourcesMeta = {
      poster: cameraMediaVideo.thumbnail_url,
      sources: {
        default: getPlayerVideoSources(cameraMediaVideo),
      },
      videoStartTime: toMoment(cameraMediaVideo.start_time),
      videoEndTime: toMoment(cameraMediaVideo.end_time),
      mediaStatus: cameraMediaVideo.media_status,
      parentEntity: cameraMediaVideo.parent_entity,
    };

    if (annotatedVideoUrl) {
      videoSourcesMeta.sources['annotated'] = [getPlayerSource(VideoSrcType.Mp4, annotatedVideoUrl)];
    }

    return videoSourcesMeta;
  };

  return {
    available: [cameraMedia.front_facing?.media_status, cameraMedia.driver_facing?.media_status]
      .includes(MEDIA_REQUEST_STATUS.SUCCESS),
    installedCameras: uniq(cameraMedia.all_cam_positions),
    frontFacing: mapCameraSource(cameraMedia.front_facing, cameraMedia.processed_videos?.road_visualizations_url),
    driverFacing: mapCameraSource(cameraMedia.driver_facing, cameraMedia.processed_videos?.distraction_tracking_url),
    frontNarrowFacing: mapCameraSource(cameraMedia.front_narrow_facing, null),
    sideLeft: mapCameraSource(cameraMedia.side_left, null),
    sideRight: mapCameraSource(cameraMedia.side_right, null),
    rear: mapCameraSource(cameraMedia.rear, null),
  };
}

export function mapCameraImages(cameraMedia: IResponseCameraMedia): ICameraImages {
  const hasImage: boolean = instanceOfImage(cameraMedia?.file_type);
  if (!cameraMedia?.available || !hasImage) return;

  return {
    frontFacing: {
      ...getPlayerSource(ImageSrcType.Jpg, cameraMedia.front_facing?.jpg_url),
      mediaStatus: cameraMedia.front_facing?.media_status,
    },
    driverFacing: {
      ...getPlayerSource(ImageSrcType.Jpg, cameraMedia.driver_facing?.jpg_url),
      mediaStatus: cameraMedia.driver_facing?.media_status,
    },
    available: cameraMedia.available,
    installedCameras: uniq(cameraMedia.all_cam_positions),
  };
}

export function mapVideoDownloadSources(videos: ICameraVideos): IPlayerDownloadSource[] {
  if (!videos) return [];

  const downloadSources: IPlayerDownloadSource[] = [];

  const isRoadVideoSuccessful: boolean = videos.frontFacing?.mediaStatus === MEDIA_REQUEST_STATUS.SUCCESS;
  const isDriverVideoSuccessful: boolean = videos.driverFacing?.mediaStatus === MEDIA_REQUEST_STATUS.SUCCESS;
  const areExternalVideosSuccessful: boolean = [videos.sideLeft, videos.sideRight, videos.rear, videos.frontNarrowFacing]
    .some((camera: IVideoSourcesMeta): boolean => camera?.mediaStatus === MEDIA_REQUEST_STATUS.SUCCESS);

  if (isRoadVideoSuccessful) {
    downloadSources.push({
      key: PIP_EVENT_PLAYER_DOWNLOAD_KEYS.ROAD,
      label: $localize`Download Road Video`,
    });
  }

  if (isRoadVideoSuccessful && isDriverVideoSuccessful) {
    downloadSources.push({
      key: PIP_EVENT_PLAYER_DOWNLOAD_KEYS.DUAL,
      label: $localize`Download Both Videos`,
    });
  }

  if (areExternalVideosSuccessful) {
    downloadSources.push({
      key: PIP_EVENT_PLAYER_DOWNLOAD_KEYS.ALL,
      label: $localize`Download All Videos`,
    });
  }

  return downloadSources;
}

export function getThumbnailUrl(cameraMedia: IResponseCameraMedia, primaryBehavior?: EventBehaviorType): string {
  if (!cameraMedia) return null;

  const cameraFacing: IResponseCameraMediaVideo = DRIVER_FACING_TAGS[primaryBehavior] && cameraMedia.driver_facing ?
    cameraMedia.driver_facing : cameraMedia.front_facing;

  return cameraFacing?.jpg_url || cameraFacing?.thumbnail_url
    || cameraMedia.side_left?.thumbnail_url
    || cameraMedia.side_right?.thumbnail_url
    || cameraMedia.rear?.thumbnail_url
    || cameraMedia.front_narrow_facing?.thumbnail_url
    || cameraMedia.thumbnail_url;
}

export function getVideoThumbnailUrl(cameraMedia: IResponseCameraMedia, primaryBehavior: EventBehaviorType): string {
  if (!cameraMedia) return null;

  const isDriverFacingBehavior: boolean = DRIVER_FACING_TAGS[primaryBehavior];
  return isDriverFacingBehavior ? cameraMedia.driver_facing?.mp4_sd_url : cameraMedia.front_facing?.mp4_sd_url;
}

export function instanceOfImage(fileType: MediaSrcType): fileType is ImageSrcType {
  return Object.values(ImageSrcType)
    .some((imageSrcType: ImageSrcType): boolean => fileType === imageSrcType);
}

export function instanceOfVideo(fileType: MediaSrcType): fileType is VideoSrcType {
  return Object.values(VideoSrcType)
    .some((videoSrcType: VideoSrcType): boolean => fileType === videoSrcType);
}

export function mapDriver(driver: IResponseDriver): IDriver {
  if (!driver) return null;

  return {
    id: driver.id,
    name: fullName(driver),
    firstName: driver.first_name,
    lastName: inDemoMode() ? replaceLastName(driver.last_name) : driver.last_name,
    email: driver.email,
    groupIds: driver.group_ids,
    groupNames: (driver.groups || []).map((group: IServerGroup): string => group.name),
    deactivatedAt: driver.deactivated_at,
    companyId: driver.driver_company_id,
  };
}

export function mapEventTags(tags: VideoTagType[]): string[] {
  if (!tags) return [];

  const videoTagLabels: string[] = tags
    .map((tag: VideoTagType): VideoTagType => CameraObstructionMap[tag] || tag)
    .filter((tag: VideoTagType): boolean => VIDEO_TAGS[tag] && !OTHER_VIDEO_TAGS[tag])
    .map((tag: VideoTagType): string => ` ${VIDEO_TAGS_LABELS[tag]}`);

  return uniq(videoTagLabels);
}

export function getRiskFactors(primaryBehavior: EventBehaviorType, secondaryBehaviors: EventBehaviorType[]): EventBehaviorType[] {
  return uniq([primaryBehavior, ...secondaryBehaviors]);
}

export function mapEventViolationMetadata(
  metadataList: IResponseViolationMetadata[],
  isSafetyEventsTrimmedT2HValuesEnabled?: boolean,
): IViolationMetadata[] {
  if (!metadataList) return [];

  return metadataList.map((metadata: IResponseViolationMetadata): IViolationMetadata => ({
    time: moment(metadata.time),
    valueInSec: isSafetyEventsTrimmedT2HValuesEnabled
      ? Math.min(metadata.value, VIOLATION_METADATA_TIME_THRESHOLD)
      : metadata.value,
  }));
}

export function getPathsOfSafetyEventIcons(
  eventType: EventType,
  isVideoAvailable: boolean,
  isTimelapse: boolean,
  isIncabAlert: boolean = false,
): string {
  if (isIncabAlert) {
    return ICONS.incabAlert.url;
  }

  if (isVideoAvailable) return '/map/dashcam.svg';

  switch (eventType) {
  case EVENT_TYPES.hard_brake:
    return '/map/hard_brake.svg';
  case EVENT_TYPES.hard_corner:
    return '/map/hard_corner.svg';
  case EVENT_TYPES.hard_accel:
    return '/map/hard_accel.svg';
  case EVENT_TYPES.aggregated_lane_swerving:
    return '/map/shield-filled.svg';
  }

  if (EVENT_TYPES[eventType]) return '/map/shield.svg';

  return isTimelapse ? '/map/requested_timelapse.svg' : '/map/requested_video.svg';
}

export function calculateDistancePerHundredUnit(value: number, total: number, metricUnits: boolean): string {
  const totalDistance: number = getDistance(total, metricUnits);
  let rate: number;
  if (totalDistance) {
    rate = +((value / totalDistance) * 100).toFixed(2);
  }
  return lodashIsFinite(rate) ? rate.toLocaleString() : '0';
}

export function getTimeByIncludingPre(time: IMoment, preSecs: number): IMoment {
  if (!time) return;

  return moment(time).subtract(preSecs || 0, 'seconds');
}

export function getTimeByIncludingPost(time: IMoment, postSecs: number): IMoment {
  if (!time) return;

  return moment(time).add(postSecs || 0, 'seconds');
}

export function mapScoreDistributionGraphData(data: IScoreDistribution): IScoreBarDistributionInputGraphData[] {
  if (!data) return;

  return [
    {
      value: data.veryPoorCount,
      color: SCORE_SCALE_COLORS.VERY_POOR,
      legendText: $localize`Very Poor`,
      legendTextInfo: `(${SCORE_SCALE.VERY_POOR}-${SCORE_SCALE.POOR - 1})`,
    },
    {
      value: data.poorCount,
      color: SCORE_SCALE_COLORS.POOR,
      legendText: $localize`Poor`,
      legendTextInfo: `(${SCORE_SCALE.POOR}-${SCORE_SCALE.FAIR - 1})`,
    },
    {
      value: data.fairCount,
      color: SCORE_SCALE_COLORS.FAIR,
      legendText: $localize`Fair`,
      legendTextInfo: `(${SCORE_SCALE.FAIR}-${SCORE_SCALE.GOOD - 1})`,
    },
    {
      value: data.goodCount,
      color: SCORE_SCALE_COLORS.GOOD,
      legendText: $localize`Good`,
      legendTextInfo: `(${SCORE_SCALE.GOOD}-${SCORE_SCALE.EXCELLENT - 1})`,
    },
    {
      value: data.excellentCount,
      color: SCORE_SCALE_COLORS.EXCELLENT,
      legendText: $localize`Excellent`,
      legendTextInfo: `(${SCORE_SCALE.EXCELLENT}-${100})`,
    },
  ];
}

export function isTimelapseVideo(endTime: string, startTime: string): boolean {
  const NORMAL_RECALL_DURATION_IN_MINUTES: number = 3;
  return moment.duration(moment(endTime).diff(moment(startTime))).asMinutes() > NORMAL_RECALL_DURATION_IN_MINUTES;
}

export function isBehaviorVisible(eventBehaviors: IEventBehaviors, behavior: EventBehaviorType): boolean {
  return !!eventBehaviors[behavior] && !ALL_EVENTS_BUCKET[0].tags.includes(behavior);
}

export function getAdditionalCamera(additionalCamera: IAdditionalCamera[], cameraPosition: CameraPosition): IAdditionalCamera {
  return additionalCamera?.find((camera: IAdditionalCamera) => camera.position === cameraPosition);
}

export function mapDownloadableVideos(downloadableVideosRes: IResponseDownloadableVideos): IDownloadableVideos {
  if (!downloadableVideosRes) return null;

  return {
    road: {
      basic: {
        url: downloadableVideosRes.front_facing_basic_url,
        aiVizUrl: downloadableVideosRes.front_facing_basic_ai_viz_url,
      },
      enhanced: {
        url: downloadableVideosRes.front_facing_enhanced_url,
        aiVizUrl: downloadableVideosRes.front_facing_enhanced_ai_viz_url,
      },
    },
    dual: {
      basic: {
        url: downloadableVideosRes.dual_facing_basic_url,
        aiVizUrl: downloadableVideosRes.dual_facing_basic_ai_viz_url,
      },
      enhanced: {
        url: downloadableVideosRes.dual_facing_enhanced_url,
        aiVizUrl: downloadableVideosRes.dual_facing_enhanced_ai_viz_url,
      },
    },
  };
}

export function mapEmsNumberInfo(data: IResponseEmsNumberInfo): IEmsNumberInfo | null {
  if (!data) return null;

  const extractCountryCode = (phoneNumber: string): string | null => {
    if (!phoneNumber) return null;

    const match: RegExpMatchArray | null = phoneNumber.match(ALLOWED_COUNTRY_CODES);
    return match ? match[0] : null;
  };

  if (inDemoMode()) {
    if (data.generated_by) data.generated_by.last_name = replaceLastName(data.generated_by.last_name);
    if (data.generated_phone_no) data.generated_phone_no = OBFUSCATED_PHONE_NUMBER;
  }

  return {
    status: data.status,
    generatedBy: data.generated_by ? fullName(data.generated_by) : null,
    generatedPhoneNo: data.generated_phone_no ? data.generated_phone_no.replace(ALLOWED_COUNTRY_CODES, '') : null,
    countryCode: extractCountryCode(data.generated_phone_no),
    generatedAt: data.generated_at,
    expiresAt: data.expires_at,
    emsInfo: data.ems_info,
  };
}

export function mapEmsNotification(
  dateFnsFormatter: DateFnsFormatter,
  updatedNotification: INotificationUpdate,
  status: STEP_STATUS = STEP_STATUS.FINISH,
): Notification {
  if (!updatedNotification) return;

  const notificationLabels: Record<NOTIFICATION_TYPE, string> = {
    ...EMS_NOTIFICATION_LABELS,
    [NOTIFICATION_TYPE.GENERATED]: `${updatedNotification.generatedBy} ${$localize`generated a new phone number for ${updatedNotification.emsInfo} emergency dispatcher`}`,
    [NOTIFICATION_TYPE.INITIATED]: `${updatedNotification.caller} ${$localize`initiated a call and collision details have been shared with dispatcher`}`,
  };

  const labelPrefix: string = `${dateFnsFormatter.format(updatedNotification.time, 'MMM d, h:mm a zzz')}:`;
  const notification: Notification = {
    label: `${labelPrefix} ${notificationLabels[updatedNotification.type]}`,
    value: updatedNotification.type,
    status,
  };

  return notification;
}

export function driverFacingMediaNotAvailable(data: IPipEventPlayerInput): boolean {
  const hasDriverFacingMedia: boolean = !!(data?.cameraVideos?.driverFacing || data?.cameraImages?.driverFacing);
  return isDualFacingCamera(data?.cameraType) && !hasDriverFacingMedia;
}

export enum VideoAttachState {
  InProgress = 'in_progress',
  Success = 'success',
  ResponseAwaited = 'response_awaited',
  UnknownError = 'unknown_error',
}

export function mapCurrentTimeRange<T extends IResponseCurrentTimeRange>(currentTimeRange: Partial<T>): ICurrentTimeRange {
  if (!currentTimeRange) {
    return { currentPeriodEnd: null, currentPeriodStart: null };
  }

  return {
    currentPeriodStart: toMoment(currentTimeRange.current_period_start),
    currentPeriodEnd: toMoment(currentTimeRange.current_period_end),
  };
}

export function getDriveScorePeriodDates<T extends Partial<ICurrentTimeRange>>(timeRange: T): IPeriodDates {
  if (!timeRange) return null;

  const previousPeriodEndDate: IMoment = moment(timeRange.currentPeriodEnd).subtract(1, 'weeks');
  const nextPeriodEndDate: IMoment = moment(timeRange.currentPeriodEnd).add(1, 'weeks');

  return {
    previousPeriodStartDate: moment(timeRange.currentPeriodStart).subtract(1, 'weeks'),
    previousPeriodEndDate: previousPeriodEndDate.isAfter(PREVIOUS_MOST_DATE) ? previousPeriodEndDate : null,
    nextPeriodEndDate: nextPeriodEndDate.isBefore(moment()) ? nextPeriodEndDate : null,
  };
}

export function getWeekRange(date: IMoment, includeNextMonday: boolean = false): IMomentRange {
  const isSunday: boolean = moment(date).day() === 0;
  const momentStartOfISOWeek: IMoment = moment(date).startOf('isoWeek'); // Monday
  const momentEndOfISOWeek: IMoment = moment(date).endOf('isoWeek'); // Sunday

  if (includeNextMonday) momentEndOfISOWeek.add(1, 'days').format(SERVER_DATE_FORMAT);

  if (isSunday) {
    return moment.range(
      momentStartOfISOWeek,
      momentEndOfISOWeek,
    );
  }
  return moment.range(
    momentStartOfISOWeek.subtract(7, 'days'),
    momentEndOfISOWeek.subtract(7, 'days'),
  );
}

export function mapGroups(groups: IServerGroup[]): IGroup[] {
  return (groups || []).map((serverGroup: IServerGroup): IGroup => ({
    id: serverGroup.id,
    name: serverGroup.name,
    totalDrivers: serverGroup.total_drivers,
  }));
}

export function mapAssignedCoach(coach: IResponseAssignedCoach): IAssignedCoach {
  return {
    id: coach.id,
    firstName: coach.first_name,
    lastName: coach.last_name,
    name: fullName(coach),
    groups: coach.groups,
  };
}

export function getWeekInfoIndicatorTooltipData(title: string, date: IMoment, count: number): IWeekInfoDotTooltip {
  return {
    tooltipSuffixText: title,
    weekStartDate: moment(date).subtract(1, 'weeks'),
    weekEndDate: moment(date).subtract(1, 'days'),
    count,
  };
}

export function getSafetyTabs(session: Session, coachingFeatureFlagService: CoachingFeatureFlagService): IPageHeaderTab[] {
  let SAFETY_TABS: IPageHeaderTab[];

  if (SAFETY_TABS) return SAFETY_TABS;

  SAFETY_TABS = [{
    label: $localize`Overview`,
    link: SAFETY_TABS_URL.OVERVIEW,
    value: TABS.Overview,
    shouldNavItemShow: session.isDriveScoreEnabled(),
  }, {
    label: $localize`Coaching`,
    link: SAFETY_TABS_URL.COACHING,
    value: TABS.Coaching,
    shouldNavItemShow: !coachingFeatureFlagService.isComplianceCoachingEnabled(),
  }, {
    label: $localize`Drivers`,
    link: SAFETY_TABS_URL.DRIVERS,
    value: TABS.Drivers,
    shouldNavItemShow: session.isDriveScoreEnabled(),
  }, {
    label: $localize`Events`,
    link: SAFETY_TABS_URL.EVENTS,
    value: TABS.Events,
    shouldNavItemShow: true,
  }, {
    label: $localize`Speeding`,
    link: SAFETY_TABS_URL.SPEEDING,
    value: TABS.Speeding,
    shouldNavItemShow: session.isSpeedingEnabled(),
  }, {
    label: $localize`Requests`,
    link: SAFETY_TABS_URL.REQUESTS,
    value: TABS.Requests,
    shouldNavItemShow: session.userPermissions.isVideoRecallReadAllowed(),
  }, {
    label: $localize`Insights`,
    link: SAFETY_TABS_URL.INSIGHTS,
    value: TABS.Insights,
    // TODO: hiding insights tab by default currently, might need to be shown in the future
    shouldNavItemShow: false && session.isDriveScoreEnabled() && !session.isSafetyScoreHidden(),
  }, {
    label: $localize`Cameras`,
    link: SAFETY_TABS_URL.CAMERAS,
    value: TABS.Cameras,
    shouldNavItemShow: session.isVideoMonitoringEnabled(),
  }];

  return SAFETY_TABS;
}

export function isSpeedBelowThreshold(speed: number, isUserMetricUnits): boolean {
  const threshold: number = isUserMetricUnits ? KPH_THRESHOLD : MPH_THRESHOLD;
  return speed < threshold;
}

export const EVENT_UNIDENTIFIED_DRIVER_TEXT: string = $localize`Driver has not been identified.`;

export const CHAT_GROUP_PARTICIPANTS_LIMIT_MESSAGE = $localize`Chats are limited to ${CHAT_CONSTANTS.MAX_GROUP_PARTICIPANTS} people`;

export function getIcon(
    settings: IVehicleBehaviorSettings | IBehaviorSettings,
    eventType: UnsafeBehaviorType,
    isEventFlagEnabled: boolean = true,
    isInCabAlertFlagEnabled: boolean = true,
  ): string {
  const alert: boolean = settings[eventType].alert.enabled;
  const event: boolean = settings[eventType].event.enabled;

  if (isInCabAlertFlagEnabled && isEventFlagEnabled) {
    if (alert && event) return getCheckboxIcon(PHX_CHECKBOX_STATES.ON);
    else if (alert || event) return getCheckboxIcon(PHX_CHECKBOX_STATES.INDETERMINATE);
    else return getCheckboxIcon(PHX_CHECKBOX_STATES.OFF);
  } else if (isInCabAlertFlagEnabled) {
    return getCheckboxIcon(alert ? PHX_CHECKBOX_STATES.ON : PHX_CHECKBOX_STATES.OFF);
  } else if (isEventFlagEnabled) {
    return getCheckboxIcon(event ? PHX_CHECKBOX_STATES.ON : PHX_CHECKBOX_STATES.OFF);
  }
}

export function getIconColor(
    settings: IVehicleBehaviorSettings | IBehaviorSettings,
    eventType: UnsafeBehaviorType,
    isEventFlagEnabled: boolean = true,
    isInCabAlertFlagEnabled: boolean = true,
  ): string {
  const alert: boolean = settings[eventType].alert.enabled;
  const event: boolean = settings[eventType].event.enabled;

  if (isInCabAlertFlagEnabled && isEventFlagEnabled) {
    return getCheckboxIconColor(alert || event);
  } else if (isInCabAlertFlagEnabled) {
    return getCheckboxIconColor(alert);
  } else if (isEventFlagEnabled) {
    return getCheckboxIconColor(event);
  }
}

export function getCheckboxIconColor(value: PHX_CHECKBOX_STATES | boolean): string {
  // using value === true because value can be a string, so we need to check for the boolean true
  if (value === PHX_CHECKBOX_STATES.ON || value === true || value === PHX_CHECKBOX_STATES.INDETERMINATE) return GREEN_60;
  else if (value === PHX_CHECKBOX_STATES.OFF || value === false) return BLACK_30;
  else return null;
}

export function getCheckboxIcon(value: PHX_CHECKBOX_STATES | boolean): string {
  // using value === true because value can be a string, so we need to check for the boolean true
  if (value === PHX_CHECKBOX_STATES.ON || value === true) return CheckCircle;
  else if (value === PHX_CHECKBOX_STATES.OFF || value === false) return CrossCircle;
  else if (value === PHX_CHECKBOX_STATES.INDETERMINATE) return SubtractCircle;
  else return null;
}

export function mapConfidentialMetadata(metadata: IResponseConfidentialMetadata): IConfidentialMetadata {
  if (!metadata) return;

  return {
    markedAt: metadata.marked_at,
    markedBy: metadata.marked_by ? fullName(metadata.marked_by) : undefined,
  };
}

export function getCameraPositionLabel(position: CameraPosition, dashcam?: CameraType): string {
  if (dashcam && isAIDashcamPlus(dashcam) && position === CameraPosition.Road) return $localize`Road - Wide`;

  return CAMERA_POSITION_TO_LABEL[position];
}